import { parse, stringify } from "qs";
import type { Ref } from "vue";
import { has, pick, trim } from "es-toolkit/compat";
import { watchPausable } from "@vueuse/core";

type BaseParams = Record<string, string | string[] | number | number[]>;

export function useSyncParams<Params extends BaseParams>(defaultParams: Params) {
  const params = ref<Params>({ ...defaultParams, ...getParamsFromSearch(location.search) });

  // Load params from URL search query
  function getParamsFromSearch(search: string) {
    const parsed = parse(search, {
      ignoreQueryPrefix: true, // removes the leading `?`
      comma: true,
    });
    const converted = Object.fromEntries(
      Object.entries(parsed).map(([key, value]) => {
        if (Array.isArray(value) || typeof value === "object") return [key, value];
        if (typeof value === "string" && !isNaN(+value)) return [key, +value];
        return [key, value];
      }),
    );
    return pick(converted, Object.keys(defaultParams)) as Partial<Params>;
  }

  // Save params to URL search query
  function getSearchFromParams(newParams: Partial<Params>) {
    const filteredParams = Object.entries(newParams).reduce((result, [key, value]) => {
      if (JSON.stringify(defaultParams[key]) === JSON.stringify(value)) return result;
      return { ...result, [key]: value };
    }, {} as Partial<Params>);
    return stringify(filteredParams, { addQueryPrefix: false, encodeValuesOnly: true });
  }

  // Serialize / deserialize params
  const serializeParams = (deserialized: Params) => JSON.stringify(deserialized);
  const deserializeParams = (serialized: string) => <Params>JSON.parse(serialized);

  // Push state
  function pushState() {
    const search = getSearchFromParams(params.value);
    const url = trim(`${location.pathname}?${search}`, "?");
    history.pushState({ params: serializeParams(params.value) }, "", url);
  }

  // Replace state
  function replaceState() {
    const search = getSearchFromParams(params.value);
    const url = trim(`${location.pathname}?${search}`, "?");
    history.replaceState({ params: serializeParams(params.value) }, "", url);
  }

  // Restore state
  async function restoreState(state: { params: string }) {
    pause();
    try {
      const newParams = { ...deserializeParams(state.params), ...getParamsFromSearch(location.search) };
      if (JSON.stringify(newParams) !== JSON.stringify(params.value)) {
        params.value = newParams;
      }
    }
    catch {
      replaceState();
    }
    await nextTick();
    resume();
  }

  // Initialize state
  async function initState() {
    if (isValidState(history.state)) {
      await restoreState(history.state);
    }
    else {
      replaceState();
    }
  }

  // Synchronize params to history state
  const { pause, resume } = watchPausable(params, pushState, { deep: true });

  // Determine if state is valid
  const isValidState = (state: unknown) =>
    has(state, "params") && typeof (<{ params: string }>state).params === "string";

  // Handle PopState events
  async function onPopState(event: PopStateEvent) {
    if (isValidState(event.state)) {
      await restoreState(event.state);
    }
  }

  onMounted(async () => {
    window.addEventListener("popstate", onPopState);
    await initState();
  });

  onUnmounted(() => {
    window.removeEventListener("popstate", onPopState);
  });

  return {
    params: <Ref<Params>>params,
    pause,
    resume,
    replaceState,
  };
}
